import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { CheckIcon, ClockIcon, TriangleAlertIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import { css, cx } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { useCurrentUser } from "utils/currentUser";

import IdeaAssessmentReport from "./IdeaAssessmentReport";
import type {
  IdeaAssessment,
  IdeaWithAssessments,
  LibIdeaChallenge,
} from "./types";

const compactButtonClass = css`
  padding: 0.85em 1.1em 0.78571429em 1em !important;

  display: inline-flex !important;
  flex-direction: row;
  flex-wrap: nowrap;

  i {
    margin: 0 !important;
  }
  i:first-child {
    font-size: 11px !important;
    margin-right: 2px !important;
  }

  .label {
    font-size: 12px !important;
    padding: 4px 8px !important;
  }

  .text-nowrap {
    display: flex;
    align-items: center;
    gap: 2px;
    font-size: 11px;
    margin-top: -1px;
  }

  &.is-compact-assessment {
    padding: 6px 9px 4px 8px !important;

    .text-nowrap {
      margin-top: -2px;
    }
  }
`;

type IdeaAssessmentProgressProps = {
  idea: IdeaWithAssessments;
  challenge?: LibIdeaChallenge;
  compact?: boolean;
};

export const IdeaAssessmentProgress = ({
  idea,
  challenge,
  compact = false,
}: IdeaAssessmentProgressProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const invitationQuery = useQuery(
    initUseQuery(api.invitations.getForType, ["ideaAssessor", idea.challenge]),
  );
  const { t } = useTranslation();

  const hasInvitedExternalAssessors =
    idea.invitedExternalAssessors && idea.invitedExternalAssessors?.length > 0;
  const hasAssessments =
    (idea.assessments && idea.assessments?.length > 0) ||
    hasInvitedExternalAssessors;
  const anyAssessmentHasConflict = idea.assessments?.some(
    (assessment: IdeaAssessment) =>
      assessment.conflictOfInterest?.hasConflictOfInterest,
  );
  const allAssessmentsAreSubmitted = idea.assessments?.every(
    (assessment: IdeaAssessment) => assessment.isSubmitted,
  );

  const user = useCurrentUser();
  const userAssessment = idea.assessments?.find(
    (assessment: IdeaAssessment) => assessment.user === user?._id,
  );

  const allAssessmentsComplete =
    allAssessmentsAreSubmitted && !hasInvitedExternalAssessors;

  const pendingInvitations = invitationQuery.data?.invitations.filter((i) =>
    i?.forIdeas?.includes(idea._id),
  );

  if (!hasAssessments) {
    return null;
  }

  return (
    <>
      <Button
        className={cx(compactButtonClass, compact && "is-compact-assessment")}
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setModalOpen(true);
        }}
        color={
          anyAssessmentHasConflict
            ? "red"
            : !allAssessmentsComplete
              ? "yellow"
              : "green"
        }
        label={
          <Label basic pointing="left">
            <span style={{ whiteSpace: "nowrap" }}>
              {idea?.assessmentDetails?.submittedCount || 0}
              {" / "}
              {(idea?.assessmentDetails?.totalCount || 0) +
                (pendingInvitations?.length || 0)}
            </span>
          </Label>
        }
      >
        {
          <div className="text-nowrap">
            <Icon
              style={{ marginRight: 3 }}
              icon={
                anyAssessmentHasConflict
                  ? TriangleAlertIcon
                  : !allAssessmentsComplete
                    ? ClockIcon
                    : CheckIcon
              }
            />
            {idea?.lockedSubmittedAssessmentScore ?? 0}%
          </div>
        }
      </Button>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="small">
        <ModalHeader>
          {t("common:capitalise", { key: "generic.idea" })} assessments
        </ModalHeader>
        <ModalContent scrolling>
          {userAssessment && !userAssessment?.isSubmitted ? (
            <Message>
              You are assigned as an assessor for this {t("generic.idea")} and
              still need to assess it.
            </Message>
          ) : null}
          <IdeaAssessmentReport idea={idea} challenge={challenge} />
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setModalOpen(false)}>Close</Button>
        </ModalActions>
      </Modal>
    </>
  );
};
